import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { LedgerAccount } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { money, payeeName } from "../../shared/lib/format";
import { reverseTransaction } from "../movement/api";
import { PagerControls } from "../../shared/ui/Pager";
import { ChipGroup } from "../../shared/ui/ChipGroup";
import { RowMenu } from "../../shared/ui/RowMenu";
import { usePager } from "../../shared/ui/usePager";
import { getAccountStatement } from "./api";
import { EmptyState } from "../../shared/ui/EmptyState";

const STATEMENT_PAGE = 25;

interface StatementPanelProps {
  bookID: string;
  accounts: LedgerAccount[];
  from: string;
  to: string;
  // Pin the statement to one account (account page); otherwise offer a picker.
  accountID?: string;
  onNotify?: (message: string) => void;
}

export function StatementPanel({ bookID, accounts, from, to, accountID: pinnedID, onNotify }: StatementPanelProps) {
  const queryClient = useQueryClient();
  const financial = accounts;
  const [selectedID, setSelectedID] = useState(financial[0]?.id ?? "");
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const accountID = pinnedID ?? (financial.some((account) => account.id === selectedID) ? selectedID : (financial[0]?.id ?? ""));
  const statement = useQuery({
    queryKey: ["statement", bookID, accountID, from, to],
    queryFn: () => getAccountStatement(bookID, accountID, from, to),
    enabled: accountID !== "",
    staleTime: 0,
    refetchOnMount: "always",
  });
  const correction = useMutation({
    mutationFn: (transactionID: string) => reverseTransaction(bookID, transactionID),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      await queryClient.invalidateQueries({ queryKey: ["statement"] });
      onNotify?.("Transaction corrected. History was preserved.");
    },
  });
  const orderedMovements = sortOrder === "newest"
    ? (statement.data?.movements ?? [])
    : [...(statement.data?.movements ?? [])].reverse();
  const pager = usePager(orderedMovements, STATEMENT_PAGE);
  // The API tells us what the running figure means. Do not infer this from
  // the public Account shape: Ledger classification is intentionally hidden.
  const owed = statement.data?.figure === "debt";
  const corrected = new Set(statement.data?.movements.flatMap((movement) => movement.transaction.references
    ?.filter((reference) => reference.kind === "reversal_of").map((reference) => reference.transactionId) ?? []) ?? []);

  function requestCorrection(transactionID: string) {
    if (window.confirm("Reverse this transaction? Its effect is cancelled, both entries stay in the audit trail, and the reversal itself can be undone.")) {
      correction.mutate(transactionID);
    }
  }

  return (
    <section className="panel statement-panel" aria-labelledby="statement-title">
      <div className="panel-heading statement-heading">
        <div>
          <p className="eyebrow">Statement</p>
          <h2 id="statement-title">{owed ? "Journal debt (not available balance)" : "Running balance"}</h2>
        </div>
        {pinnedID ? null : (
          <div className="statement-account-tabs" role="tablist" aria-label="Account">
            {financial.map((account) => (
              <button
                key={account.id}
                type="button"
                role="tab"
                aria-selected={account.id === accountID}
                className="chip account-tab"
                onClick={() => setSelectedID(account.id)}
              >
                <span>{account.name}</span>{account.name.toUpperCase().endsWith(` ${account.currency}`) ? null : <small>{account.currency}</small>}
              </button>
            ))}
          </div>
        )}
      </div>
      {statement.isPending && accountID ? <p className="panel-note">Calculating statement…</p> : null}
      {statement.isError ? <p className="form-error" role="alert">{humanError(statement.error)}</p> : null}
      {statement.data ? (
        <>
          <dl className="figure-strip">
            <div>
              <dt>{owed ? "Journal debt at start" : "Opening"}</dt>
              <dd>{money(owed ? -statement.data.openingBalance.minor : statement.data.openingBalance.minor, statement.data.openingBalance.currency)}</dd>
            </div>
            <div>
              <dt>{owed ? "Journal debt at end" : "Closing"}</dt>
              <dd>{money(owed ? -statement.data.closingBalance.minor : statement.data.closingBalance.minor, statement.data.closingBalance.currency)}</dd>
            </div>
          </dl>
          <div className="statement-head" aria-hidden="true">
            <span>Date</span><span>Description</span>
                    <span>{owed ? "Journal change" : "Change"}</span>
                    <span>{owed ? "Journal debt" : "Balance"}</span><span />
          </div>
          <div className="statement-sort">
            <ChipGroup
              label="Transaction order"
              value={sortOrder}
              options={[
                { value: "newest", label: "Newest first" },
                { value: "oldest", label: "Oldest first" },
              ]}
              onChange={(value) => setSortOrder(value as "newest" | "oldest")}
            />
          </div>
          <div className="statement-list">
            {pager.visible.map((movement) => {
              const isCorrection = movement.transaction.references?.some((reference) => reference.kind === "reversal_of") ?? false;
              const wasCorrected = corrected.has(movement.transaction.id);
              const description = movement.transaction.description || "Transaction";
              const when = new Intl.DateTimeFormat("en", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(movement.transaction.effectiveAt));
              const note = `${when}${isCorrection ? " · correction" : wasCorrected ? " · corrected" : ""}`;
              return (
                <article className="statement-row" key={movement.transaction.id}>
                  <time className="statement-when" dateTime={movement.transaction.effectiveAt} title={note}>{when}</time>
                  <span className="statement-copy">
                    <strong title={description}>{payeeName(description)}</strong>
                    {isCorrection || wasCorrected ? <small>{isCorrection ? "correction" : "corrected"}</small> : null}
                  </span>
                  <span
                    className={(owed ? movement.balanceDelta.minor < 0 : movement.balanceDelta.minor >= 0) ? "delta-positive" : "delta-negative"}
                  >
                    {money(owed ? -movement.balanceDelta.minor : movement.balanceDelta.minor, movement.balanceDelta.currency, true)}
                  </span>
                  <span className="statement-running" title={owed ? "Journal debt after this movement; not provider available funds" : "Balance after this movement"}>
                    {money(owed ? -movement.runningBalance.minor : movement.runningBalance.minor, movement.runningBalance.currency)}
                  </span>
                  {onNotify && !isCorrection && !wasCorrected ? (
                    <RowMenu label={`Actions for ${description}`}>
                      <button className="row-menu-item danger" type="button" onClick={() => requestCorrection(movement.transaction.id)} disabled={correction.isPending}>Reverse this transaction</button>
                    </RowMenu>
                  ) : <span className="row-menu-spacer" />}
                </article>
              );
            })}
            {statement.data.movements.length === 0 ? <EmptyState>This account has no movements in the chosen period. Pick a wider period, or import a statement for it.</EmptyState> : null}
            {statement.data.movements.length === 0 && statement.data.openingBalance.minor !== 0 ? (
              <p className="panel-note">The opening amount carries movements recorded before this period. Change the period to see those entries.</p>
            ) : null}
          </div>
          <PagerControls pager={pager} noun="movements" step={STATEMENT_PAGE} />
          {correction.error ? <p className="form-error" role="alert">{humanError(correction.error)}</p> : null}
        </>
      ) : null}
    </section>
  );
}
